<script setup lang="ts">
import { useProductPrice } from "@shopware-pwa/composables-next";
import type { Product } from "@shopware-pwa/types";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import { Tint } from "~/components/clarity-ui/Tint";
import { FormattedCurrency } from "~/components/ui/FormattedCurrency";
import { FormattedPercent } from "~/components/ui/FormattedPercent";
import { useExchangeBox } from "~/composables/shop/useExchangeBoxProduct";
import CommonLabel from "~/templates/components/CommonLabel.vue";

export interface ProductPriceProps {
  product: Product;
}

const props = defineProps<ProductPriceProps>();
const { product } = toRefs(props);

const { price, unitPrice, isListPrice } = useProductPrice(product);

const { containsExchangeBox, amountCylinders } = await useExchangeBox();
</script>

<template>
  <div>
    <div class="flex items-end gap-3">
      <SharedPrice
        v-if="unitPrice"
        class="text-2xl font-medium lg:text-3xl"
        :discounted="isListPrice"
        :value="unitPrice"
      />
      <SharedPrice
        v-if="isListPrice"
        class="lg:text-lg"
        strikethrough
        :value="price?.listPrice?.price"
      />
    </div>
    <Paragraph v-if="isListPrice" class="mt-1 flex gap-1" size="small">
      <CommonLabel path="BWF/shop/productDetailPage/priceSaving" />
      <SharedPrice v-if="isListPrice" :value="price?.listPrice?.discount" />
      (<FormattedPercent :value="Math.abs(price?.listPrice?.percentage ?? 0)" :precision="0" />)
    </Paragraph>
    <Tint class="mt-1 text-sm" tag="p">
      <template v-if="containsExchangeBox">
        <CommonLabel
          :placeholder="{ amountCylinders }"
          path="BWF/shop/exchangeBox/priceCardExchangeBoxHint"
        />&nbsp;</template
      >
      <span v-if="product?.unit?.name">
        <span v-if="product?.packUnit">{{ product?.packUnit }}&nbsp;</span>
        <span v-else>{{ product?.purchaseUnit }}&nbsp;{{ product?.unit?.shortCode }}&nbsp;</span>
        (<FormattedCurrency
          :value="product?.calculatedPrice.referencePrice?.price"
        />&nbsp;/&nbsp;{{ product?.calculatedPrice.referencePrice?.referenceUnit
        }}{{ product?.unit?.shortCode }})&nbsp;·&nbsp;</span
      >
      <CommonLabel path="BWF/shop/productDetailPage/plus" />&nbsp;<CommonLabel
        path="BWF/shop/checkout/cart/taxes"
      />
    </Tint>
  </div>
</template>
